import { 
  Br, 
  Cut, 
  Line, 
  Printer, 
  Text, 
  Row, 
  render, 
  Cashdraw 
} from 'react-thermal-printer';
import { PointOfSaleTransaction } from '../../model/pointofsale/PointOfSaleTransaction';
import { MemberPromotion } from '../../model/pointofsale/MemberPromotion';
import { Booking } from '../../model/bookings/Booking';
import { Slot } from '../../model/bookings/Slot';
import { Facility } from '../../model/facilities/Facility';
import { formatCurrency, formatDate, formatDateTime, formatTime } from '../../utils/formatters';
import { escapeHtml } from '../../utils/escapeHtml';

export interface ReceiptData {
  transactionId?: string;
  memberName: string;
  membershipId: string;
  timestamp: Date;
  items: Array<{
    name: string;
    quantity: number;
    unitPrice: number;
    total: number;
  }>;
  facilityBookings?: Array<{
    facilityName: string;
    startTime: Date;
    endTime: Date;
    price: number;
  }>;
  subtotal: number;
  total: number;
  status: string;
  clubName?: string;
}

export class ReceiptPrinterService {
  private printerName: string;
  private clubName: string;
  private printerType: 'epson' | 'star';
  private printerWidth: number;
  private currency: string;
  private timezone: string;
  private usbDevice: any = null; // Store the USB device for reuse

  constructor(
    printerName: string = 'EPSON TM-T88VI',
    clubName: string = 'Southbank Club',
    printerType: 'epson' | 'star' = 'epson',
    printerWidth: number = 42,
    currency: string = 'GBP',
    timezone: string = 'Europe/London'
  ) {
    this.printerName = printerName;
    this.clubName = clubName;
    this.printerType = printerType;
    this.printerWidth = printerWidth;
    this.currency = currency;
    this.timezone = timezone;
    
    // Validate printer width for common thermal printer sizes
    if (printerWidth < 32 || printerWidth > 48) {
      console.warn(`Printer width ${printerWidth} may not be optimal for thermal printers. Standard widths are 32 (58mm), 42 (80mm), or 48 (112mm)`);
    }
  }

  /**
   * Print a receipt using the thermal printer library
   */
  async printReceipt(receiptData: ReceiptData, openCashDrawer: boolean = false): Promise<boolean> {
    try {
      const receiptElement = this.generateReceiptElement(receiptData, openCashDrawer);
      const data: Uint8Array = await render(receiptElement);
      
      // Try USB printing first (only if device is already connected)
      if (this.usbDevice) {
        try {
          const usbResult = await this.printViaUSB(data);
          if (usbResult) {
            return true; // USB printing successful
          }
        } catch (error) {
          console.log('USB printing failed, trying to reconnect...');
          // Try to reconnect the USB device
          try {
            await this.usbDevice.close();
            await this.usbDevice.open();
            await this.usbDevice.selectConfiguration(1);
            await this.usbDevice.claimInterface(0);
            
            // Try USB printing again after reconnection
            const retryResult = await this.printViaUSB(data);
            if (retryResult) {
              return true; // USB printing successful after reconnection
            }
          } catch (reconnectError) {
            console.log('USB reconnection failed:', reconnectError);
            this.usbDevice = null; // Clear the device on error
          }
        }
      }
      
      // Only use browser fallback if explicitly requested or no USB device available
      if (this.shouldAllowBrowserPrinting() || !this.usbDevice) {
        console.log('Using browser print fallback...');
        return await this.printViaBrowser(receiptData);
      }
      
      // If we have a USB device but printing failed, return false instead of falling back to browser
      return false;
    } catch (error) {
      console.error('Error printing receipt:', error);
      
      // Only use browser fallback in development or if explicitly requested
      if (this.shouldAllowBrowserPrinting()) {
        return await this.printViaBrowser(receiptData);
      }
      
      return false;
    }
  }

  /**
   * Print via Web USB API (for direct thermal printer connection)
   */
  private async printViaUSB(data: Uint8Array): Promise<boolean> {
    try {
      if (!this.usbDevice) {
        throw new Error('No USB device connected');
      }

      // Check if device is still open, if not, reopen it
      if (!this.usbDevice.opened) {
        await this.usbDevice.open();
        await this.usbDevice.selectConfiguration(1);
        await this.usbDevice.claimInterface(0);
      }

      // Find the appropriate endpoint for data transfer
      const usbInterface = this.usbDevice.configuration.interfaces[0];
      const alternate = usbInterface.alternates[0];
      const endpoint = alternate.endpoints.find((ep: any) => 
        ep.direction === 'out' && ep.type === 'bulk'
      );

      if (!endpoint) {
        throw new Error('No suitable USB endpoint found for printing');
      }

      // Send data to the printer in chunks if needed
      const chunkSize = 64; // USB bulk transfer chunk size
      for (let i = 0; i < data.length; i += chunkSize) {
        const chunk = data.slice(i, i + chunkSize);
        await this.usbDevice.transferOut(endpoint.endpointNumber, chunk);
      }
      
      return true;
    } catch (error) {
      console.error('USB printing failed:', error);
      this.usbDevice = null; // Clear the device on error
      return false;
    }
  }



  /**
   * Fallback to browser print API
   */
  private async printViaBrowser(receiptData: ReceiptData): Promise<boolean> {
    try {
      const receiptContent = this.generateReceiptContent(receiptData);
      const printWindow = window.open('', '_blank');
      
      if (!printWindow) {
        throw new Error('Popup blocked. Please allow popups for this site.');
      }

      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Receipt</title>
          <style>
            @media print {
              @page {
                margin: 0;
                size: ${this.getPaperWidth()}mm auto;
              }
              body {
                margin: 0;
                padding: 10px;
                font-family: 'Courier New', monospace;
                font-size: 12px;
                line-height: 1.2;
                width: ${this.getPaperWidth()}mm;
                max-width: ${this.getPaperWidth()}mm;
              }
              .receipt {
                width: 100%;
                text-align: center;
              }
              .header {
                font-weight: bold;
                font-size: 14px;
                margin-bottom: 10px;
              }
              .divider {
                border-top: 1px dashed #000;
                margin: 10px 0;
              }
              .item {
                display: flex;
                justify-content: space-between;
                margin: 2px 0;
                font-size: 11px;
              }
              .item-name {
                flex: 1;
                text-align: left;
              }
              .item-price {
                text-align: right;
                min-width: 60px;
              }
              .total {
                font-weight: bold;
                font-size: 14px;
                margin-top: 10px;
              }
              .footer {
                margin-top: 20px;
                font-size: 10px;
                color: #666;
              }
              .status {
                font-weight: bold;
                margin: 10px 0;
              }
              .status.completed {
                color: #28a745;
              }
              .status.unpaid {
                color: #dc3545;
              }
            }
          </style>
        </head>
        <body>
          ${receiptContent}
        </body>
        </html>
      `);

      printWindow.document.close();
      
      // Wait for content to load
      await new Promise(resolve => setTimeout(resolve, 500));
      
      printWindow.print();
      
      // Close the window after printing
      setTimeout(() => {
        printWindow.close();
      }, 1000);

      return true;
    } catch (error) {
      console.error('Error printing receipt:', error);
      return false;
    }
  }

  /**
   * Generate receipt as React element for thermal printer
   */
  private generateReceiptElement(receiptData: ReceiptData, openCashDrawer: boolean = false) {
    const timestamp = receiptData.timestamp;
    const dateStr = formatDate(timestamp, this.timezone);
    const timeStr = formatTime(timestamp, this.timezone);

    return (
      <Printer type={this.printerType} width={this.printerWidth}>
        {/* Header */}
        <Text size={{ width: 2, height: 2 }} align="center">
          {this.clubName}
        </Text>
        <Br />
        <Line />
        
        {/* Receipt dimensions info for debugging */}
        {process.env.NODE_ENV === 'development' && (
          <Text size={{ width: 1, height: 1 }} align="center">
            Width: {this.printerWidth} chars ({this.getPaperWidth()}mm)
          </Text>
        )}
        
        {/* Member Information — name and membership ID rendered on one line */}
        <Row left="Member" right={`${receiptData.memberName} | ${receiptData.membershipId}`} />
        <Row left="Date" right={dateStr} />
        <Row left="Time" right={timeStr} />
        {receiptData.transactionId && (
          <Row left="Transaction" right={receiptData.transactionId} />
        )}
        <Br />
        <Line />
        
        {/* Items */}
        {receiptData.items.map((item, index) => (
          <div key={index}>
            <Row
              left={`${item.name} x${item.quantity}`}
              right={this.formatMoney(item.total)}
            />
            <Text size={{ width: 1, height: 1 }}>
              {`  ${this.formatMoney(item.unitPrice)} each`}
            </Text>
          </div>
        ))}

        {/* Facility Bookings */}
        {receiptData.facilityBookings && receiptData.facilityBookings.map((booking, index) => {
          const startTime = formatTime(booking.startTime, this.timezone);
          const endTime = formatTime(booking.endTime, this.timezone);

          return (
            <div key={`booking-${index}`}>
              <Row
                left={`${booking.facilityName} (${startTime}-${endTime})`}
                right={this.formatMoney(booking.price)}
              />
            </div>
          );
        })}

        <Line />

        {/* Total */}
        <Row
          left={<Text bold={true}>TOTAL</Text>}
          right={<Text bold={true}>{this.formatMoney(receiptData.total)}</Text>}
        />
        <Br />
        
        {/* Status */}
        <Text align="center" bold={true}>
          Status: {receiptData.status.toUpperCase()}
        </Text>
        <Br />
        
        {/* Footer */}
        <Line />
        <Text align="center" size={{ width: 1, height: 1 }}>
          Thank you for your business!
        </Text>
        <Text align="center" size={{ width: 1, height: 1 }}>
          Generated on {formatDateTime(new Date(), this.timezone)}
        </Text>
        <Br />
        <Br />
        
        {/* Cash Drawer */}
        {openCashDrawer && <Cashdraw pin="2pin" />}
        
        {/* Cut Paper */}
        <Cut />
      </Printer>
    );
  }

  /**
   * Generate receipt content in HTML format (fallback)
   */
  private generateReceiptContent(receiptData: ReceiptData): string {
    const timestamp = receiptData.timestamp;
    const dateStr = formatDate(timestamp, this.timezone);
    const timeStr = formatTime(timestamp, this.timezone);

    let itemsHtml = '';
    receiptData.items.forEach(item => {
      itemsHtml += `
        <div class="item">
          <span class="item-name">${escapeHtml(item.name)} x${item.quantity}</span>
          <span class="item-price">${this.formatMoney(item.total)}</span>
        </div>
      `;
    });

    let facilityBookingsHtml = '';
    if (receiptData.facilityBookings && receiptData.facilityBookings.length > 0) {
      facilityBookingsHtml = '<div class="divider"></div>';
      receiptData.facilityBookings.forEach(booking => {
        const startTime = formatTime(booking.startTime, this.timezone);
        const endTime = formatTime(booking.endTime, this.timezone);

        facilityBookingsHtml += `
          <div class="item">
            <span class="item-name"><strong>${escapeHtml(booking.facilityName)} (${startTime}-${endTime})</strong></span>
            <span class="item-price"><strong>${this.formatMoney(booking.price)}</strong></span>
          </div>
        `;
      });
    }

    const statusClass = receiptData.status.toLowerCase() === 'completed' ? 'completed' : 'unpaid';

    return `
      <div class="receipt">
        <div class="header">${escapeHtml(this.clubName)}</div>
        <div class="divider"></div>

        <div style="text-align: left; margin: 10px 0;">
          <div><strong>Member: ${escapeHtml(receiptData.memberName)} | ${escapeHtml(receiptData.membershipId)}</strong></div>
          <div><strong>Date: ${dateStr}</strong></div>
          <div><strong>Time: ${timeStr}</strong></div>
          ${receiptData.transactionId ? `<div><strong>Transaction: ${escapeHtml(receiptData.transactionId)}</strong></div>` : ''}
        </div>
        
        <div class="divider"></div>
        
        ${itemsHtml}
        ${facilityBookingsHtml}
        
        <div class="divider"></div>
        
        <div class="item">
          <span class="item-name"><strong>Total</strong></span>
          <span class="item-price"><strong>${this.formatMoney(receiptData.total)}</strong></span>
        </div>
        
        <div class="status ${statusClass}">
          Status: ${escapeHtml(receiptData.status.toUpperCase())}
        </div>
        
        <div class="footer">
          Thank you for your business!
          <br>
          Generated on ${formatDateTime(new Date(), this.timezone)}
        </div>
      </div>
    `;
  }

  /**
   * Get the paper width in mm based on printer width
   */
  private getPaperWidth(): number {
    // Standard thermal printer paper widths
    switch (this.printerWidth) {
      case 32: return 58;   // 58mm paper
      case 42: return 80;   // 80mm paper (most common)
      case 48: return 112;  // 112mm paper
      default: return 80;   // Default to 80mm
    }
  }

  /**
   * Ensure a value is a number
   */
  private ensureNumber(value: any): number {
    if (typeof value === 'string') {
      return parseFloat(value) || 0;
    }
    if (typeof value === 'number') {
      return value;
    }
    return 0;
  }

  private formatMoney(value: any): string {
    return formatCurrency(this.ensureNumber(value), this.currency);
  }

  /**
   * Connect to a USB device (must be called from a user gesture)
   */
  async connectUSBDevice(): Promise<boolean> {
    try {
      // Type assertion for Web USB API
      const usb = (navigator as any).usb;
      if (!usb) {
        throw new Error('Web USB API not available');
      }

      // Request USB device access (this must be called from a user gesture)
      const device = await usb.requestDevice({
        filters: [
          // Common thermal printer vendor IDs
          { vendorId: 0x04b8 }, // Epson
          { vendorId: 0x0519 }, // Star
          { vendorId: 0x0483 }, // STMicroelectronics (some thermal printers)
          { vendorId: 0x1a86 }, // QinHeng Electronics (some thermal printers)
          // Add more vendor IDs as needed for your specific printer
        ]
      });

      await device.open();
      await device.selectConfiguration(1);
      await device.claimInterface(0);

      this.usbDevice = device;
      return true;
    } catch (error) {
      console.error('USB device connection failed:', error);
      this.usbDevice = null;
      return false;
    }
  }

  /**
   * Disconnect the USB device
   */
  async disconnectUSBDevice(): Promise<void> {
    if (this.usbDevice && this.usbDevice.opened) {
      try {
        await this.usbDevice.close();
      } catch (error) {
        console.error('Error closing USB device:', error);
      }
    }
    this.usbDevice = null;
  }

  /**
   * Check if USB device is connected
   */
  isUSBDeviceConnected(): boolean {
    return this.usbDevice !== null && this.usbDevice.opened;
  }

  /**
   * Force print using browser (bypasses USB printing)
   */
  async forcePrintViaBrowser(receiptData: ReceiptData): Promise<boolean> {
    return await this.printViaBrowser(receiptData);
  }

  /**
   * Check if USB printing is available and working
   */
  async testUSBConnection(): Promise<boolean> {
    if (!this.usbDevice) {
      return false;
    }

    try {
      if (!this.usbDevice.opened) {
        await this.usbDevice.open();
        await this.usbDevice.selectConfiguration(1);
        await this.usbDevice.claimInterface(0);
      }
      return true;
    } catch (error) {
      console.error('USB connection test failed:', error);
      return false;
    }
  }

  /**
   * Check if browser printing should be allowed
   */
  private shouldAllowBrowserPrinting(): boolean {
    // Always allow browser printing as a fallback
    return true;
  }

  /**
   * Get available USB devices for debugging printer connection
   */
  static async getAvailableUSBDevices(): Promise<any[]> {
    try {
      const usb = (navigator as any).usb;
      if (!usb) {
        throw new Error('Web USB API not available');
      }

      const devices = await usb.getDevices();
      return devices.map((device: any) => ({
        vendorId: device.vendorId,
        productId: device.productId,
        manufacturerName: device.manufacturerName,
        productName: device.productName,
        serialNumber: device.serialNumber
      }));
    } catch (error) {
      console.error('Error getting USB devices:', error);
      return [];
    }
  }

  /**
   * Request USB device access and return device info
   */
  static async requestUSBDevice(): Promise<any> {
    try {
      const usb = (navigator as any).usb;
      if (!usb) {
        throw new Error('Web USB API not available');
      }

      const device = await usb.requestDevice({
        filters: [
          // Common thermal printer vendor IDs
          { vendorId: 0x04b8 }, // Epson
          { vendorId: 0x0519 }, // Star
          { vendorId: 0x0483 }, // STMicroelectronics (some thermal printers)
          { vendorId: 0x1a86 }, // QinHeng Electronics (some thermal printers)
          // Add more vendor IDs as needed for your specific printer
        ]
      });

      return {
        vendorId: device.vendorId,
        productId: device.productId,
        manufacturerName: device.manufacturerName,
        productName: device.productName,
        serialNumber: device.serialNumber
      };
    } catch (error) {
      console.error('Error requesting USB device:', error);
      throw error;
    }
  }

  /**
   * Create receipt data from a Point of Sale transaction
   */
  static createReceiptDataFromTransaction(
    transaction: PointOfSaleTransaction,
    memberName: string,
    clubName?: string
  ): ReceiptData {
    const items = transaction.items.map(item => ({
      name: item.item.name,
      quantity: item.quantity,
      unitPrice: item.item.price,
      total: item.item.price * item.quantity
    }));

    const facilityBookings = transaction.bookingSlots.map(slot => ({
      facilityName: slot.bookingSlot.facilityName,
      startTime: slot.bookingSlot.startTime,
      endTime: slot.bookingSlot.endTime,
      price: slot.price
    }));

    return {
      transactionId: transaction.transactionId?.toString(),
      memberName: memberName || 'Unknown Member',
      membershipId: transaction.membershipId,
      timestamp: transaction.timestamp,
      items,
      facilityBookings,
      subtotal: transaction.amount,
      total: transaction.amount,
      status: transaction.status,
      clubName
    };
  }

  /**
   * Create receipt data from an applied promotion (FORT-318).
   *
   * Built from loose pieces rather than a PointOfSaleTransaction: applying a promotion writes its
   * POS rows server-side and returns the history row, not a transaction. The single line item is
   * the promotion's POS item at quantity 1, which is exactly what the apply repository records.
   */
  static createReceiptDataFromPromotion(
    applied: MemberPromotion,
    posItemName: string,
    memberName: string,
    membershipId: string,
    status: string,
    clubName?: string
  ): ReceiptData {
    return {
      transactionId: applied.paymentTransactionId?.toString(),
      memberName: memberName || 'Unknown Member',
      membershipId,
      // appliedOn is a DateOnly, but receipts print a wall-clock time, so stamp it here.
      timestamp: new Date(),
      items: [{
        name: posItemName,
        quantity: 1,
        unitPrice: applied.amountCharged,
        total: applied.amountCharged
      }],
      facilityBookings: [],
      subtotal: applied.amountCharged,
      total: applied.amountCharged,
      status,
      clubName
    };
  }

  /**
   * Create receipt data from a booking check-in
   */
  static createReceiptDataFromBooking(
    booking: Booking,
    slot: Slot,
    facility: Facility,
    items: Array<{ item: any; quantity: number; total: number }>,
    total: number,
    status: string,
    clubName?: string
  ): ReceiptData {
    const posItems = items.map(item => ({
      name: item.item.name,
      quantity: item.quantity,
      unitPrice: item.item.price,
      total: item.total
    }));

    // Ensure price is converted to a number
    const slotPrice = slot.metadata?.Price;
    const numericPrice = typeof slotPrice === 'string' ? parseFloat(slotPrice) : 
                        typeof slotPrice === 'number' ? slotPrice : 0;

    const facilityBookings = [{
      facilityName: facility.name,
      startTime: booking.bookingDateTimeUtc,
      endTime: booking.bookingEndTimeUtc,
      price: numericPrice
    }];

    return {
      memberName: booking.memberName,
      membershipId: booking.membershipId,
      timestamp: new Date(),
      items: posItems,
      facilityBookings,
      subtotal: total,
      total,
      status,
      clubName
    };
  }
} 